import { useState } from "react";
import RadioGroupAnswer from "../answer/RadioGroupAnswer";
import reactStringReplace from "react-string-replace";
import { TTask, CheckedCorrect, TChecked } from "../../types";
import { useCourseContext } from "../../context/courseContext";
import { AnswerTypeEnum } from "../../enums";
import { useAuthContext } from "../../context/authContext";
import { MathJaxOutput } from "../mathJax/MathJaxOutput";
import { transformBrToNHelper } from "../../helpers/transformBrToNHelper";
import classNames from "classnames";
import InputAnswer from "../answer/InputAnswer";
import TaskEditButton from "./TaskEditButton";
import { QuestionImage } from "./QuestionImage";
import { API_URL } from "../../APIService";
import { isNoRoles } from "../../helpers/role";
import CheckboxGroupAnswer from "../answer/CheckboxGroupAnswer";

export type TTaskItemProps = {
  task: TTask;
  answerList: any;
  checked?: TChecked;
  isCheckButtonClicked: boolean;
  isCorrectlySolved: boolean;
};

function TaskItem(props: TTaskItemProps) {
  const { task, checked, answerList, isCheckButtonClicked, isCorrectlySolved } =
    props;
  const defaultWidth = 80;
  const maxWidth = 300;
  const [inputWidth, setInputWidth] = useState(defaultWidth);

  const expandShrinkField = (e: any) => {
    let length = e.target.value.length;
    if (length === 0) {
      setInputWidth(defaultWidth);
    }
    if (inputWidth <= maxWidth && e.target.value.length * 10 >= inputWidth) {
      setInputWidth(defaultWidth + e.target.value.length * 5);
    }
  };

  const mask = task.answerMask || "";

  const inputAlgAndRegexp = () => {
    let count = -1;

    const onChange = (e: any) => {
      handleChange(e);
      expandShrinkField(e);
    };

    const onChangeRationals = (e: any) => {
      if (RegExp("[^0-9,;\/\-]", "i").exec(e.target.value)) return;
      handleChange(e);
      expandShrinkField(e);
    };

    const onChangeInts = (e: any) => {
      if (RegExp("[^0-9;\-]", "i").exec(e.target.value)) return;
      handleChange(e);
      expandShrinkField(e);
    };

    return (
      <MathJaxOutput
        text={reactStringReplace(
          transformBrToNHelper(mask),
          /(%d|%s|%i)/g,
          (match: string, i: number) => {
            count++;
            const elementId = "id" + task.id + "_" + count;
            return (
              <div
                key={elementId}
                className={classNames(["TaskRegExpAnswer", "w-25"])}
              >
                <InputAnswer
                  value={answerList[elementId] || ""}
                  disabled={isCheckButtonClicked}
                  key={elementId}
                  id={elementId}
                  onChange={
                    match === "%d"
                      ? onChangeRationals
                      : match === "%i"
                        ? onChangeInts
                        : onChange
                  }
                  inputwidth={inputWidth}
                />
              </div>
            );
          },
        )}
      />
    );
  };

  const inputSimpleAlg2Nospaces = (
    label?: string,
    defaultAnswer?: any,
    allowedChars?: string,
  ) => {
    const s = allowedChars ? allowedChars : "[^0-9,;\/\-]";

    const onChange2 = (e: any) => {
      if (RegExp(s, "i").exec(e.target.value)) return;
      handleChange(e);
      expandShrinkField(e);
    };

    return (
      <div key={task.id} className={classNames(["TaskRegExpAnswer", "w-75"])}>
        <InputAnswer
          disableunderline="true"
          value={
            answerList["id" + task.id]
              ? answerList["id" + task.id]
              : defaultAnswer || ""
          }
          disabled={isCheckButtonClicked}
          id={"id" + task.id}
          placeholder={label}
          onChange={onChange2}
        />
      </div>
    );
  };

  const { handleChange } = useCourseContext();
  const { authInfo } = useAuthContext();

  const correct = checked && checked.isCorrect === CheckedCorrect.CORRECT;
  const hintStyle = correct ? "TaskHintCorrect" : "TaskHintWrong";
  const answerStyle =
    isCheckButtonClicked && checked
      ? correct
        ? "TaskAnswerBlockCorrect"
        : "TaskAnswerBlockWrong"
      : task.answerType == AnswerTypeEnum.NONE
        ? "TaskAnswerBlockNoBorder"
        : "TaskAnswerBlock";

  return (
    <div className="TaskInnerLayout">
      <div className="TaskInformation">
        <QuestionImage task={task} maxWidth={maxWidth} maxHeight={400} />

        <MathJaxOutput text={transformBrToNHelper(task.question)} />

        <p key={task.id} className="TaskTaskCode">
          (код задачи: {task.id})
          <TaskEditButton access={authInfo.role} id={task.id} />
        </p>
      </div>

      <div key={"id" + task.id} className="TaskTask">
        {isNoRoles(authInfo.role) &&
        (task.isAlreadySolved ||
          (!isCheckButtonClicked && isCorrectlySolved)) ? (
          <div
            className={
              task.answerType == AnswerTypeEnum.NONE
                ? "TaskAnswerBlockNoBorder"
                : "TaskAnswerBlock"
            }
          >
            <p className="TaskAlreadySolved">Правильно решено ранее.</p>
          </div>
        ) : (
          <div key={"id" + task.id} className={answerStyle}>
            {(() => {
              switch (task.answerType) {
                case AnswerTypeEnum.RADIO:
                  return (
                    <RadioGroupAnswer
                      answer={answerList["id" + task.id]}
                      disabled={isCheckButtonClicked}
                      key={task.id}
                      id={"id" + task.id}
                      answerlist={task.answerMask.split(";")}
                      onChange={handleChange}
                    />
                  );

                case AnswerTypeEnum.CHECKBOX:
                  return (
                    <CheckboxGroupAnswer
                      answer={answerList["id" + task.id]}
                      disabled={isCheckButtonClicked}
                      key={task.id}
                      id={"id" + task.id}
                      answerlist={task.answerMask.split(";")}
                      onChange={handleChange}
                    />
                  );

                case AnswerTypeEnum.SIMPLE:
                  return (
                    <div className="TaskAnswerArea">
                      {inputSimpleAlg2Nospaces("Ответ")}
                    </div>
                  );

                case AnswerTypeEnum.ALG:
                case AnswerTypeEnum.REG_EXP:
                  return (
                    <div className="TaskAnswerArea">{inputAlgAndRegexp()}</div>
                  );

                case AnswerTypeEnum.ALG2:
                  return (
                    <div className="TaskAnswerArea">
                      {inputSimpleAlg2Nospaces("Ответ через ;")}
                    </div>
                  );

                case AnswerTypeEnum.NO_SPACES:
                  return (
                    <div className="TaskAnswerArea">
                      {inputSimpleAlg2Nospaces(
                        "Введите выражение",
                        task.answerMask,
                        "[^0-9a-z ,а-яА-Я;\/\+\*\=\-]",
                      )}
                    </div>
                  );
                default: {
                  return <div className="TaskAnswerArea"></div>;
                }
              }
            })()}
            {task.isAlreadySolved ||
            (!isCheckButtonClicked && isCorrectlySolved) ? (
              <p className="TaskAlreadySolvedSmall">Правильно решено ранее.</p>
            ) : (
              <></>
            )}
          </div>
        )}
      </div>

      {isCheckButtonClicked && checked ? (
        <div className={hintStyle}>
          {correct && checked.solutionImage ? (
            <p>
              <img
                src={API_URL + "image" + checked.solutionImage}
                alt={checked.solutionImage}
              />
            </p>
          ) : (
            <></>
          )}
          {checked.hint
            ? checked.hint.map((h) => (
                <div>
                  {h.image && h.image.length > 0 ? (
                    <p>
                      <img src={API_URL + "image" + h.image} alt={h.image} />
                    </p>
                  ) : null}
                  {h.note ? (
                    <p>
                      <MathJaxOutput text={h.note} />
                    </p>
                  ) : null}
                </div>
              ))
            : null}
        </div>
      ) : (
        <></>
      )}
    </div>
  );
}

export default TaskItem;
